import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useOnboarding } from "../context/OnboardingContext";
import { useEffectivePermissions, toSet, can, canAny } from "../hooks/useEffectivePermissions";

const CORE_STEPS = [
  {
    id: "step-guided-setup",
    labelKey: "checklist.steps.guidedSetup.label",
    descriptionKey: "checklist.steps.guidedSetup.description",
    route: "/setup",
  },
  {
    id: "step-connect-channels",
    labelKey: "checklist.steps.channels.label",
    descriptionKey: "checklist.steps.channels.description",
    route: "/settings/channels",
  },
  {
    id: "step-create-users",
    labelKey: "checklist.steps.users.label",
    descriptionKey: "checklist.steps.users.description",
    route: "/settings/users",
  },
  {
    id: "step-setup-ai",
    labelKey: "checklist.steps.ai.label",
    descriptionKey: "checklist.steps.ai.description",
    route: "/agents",
  },
  {
    id: "step-first-conversation",
    labelKey: "checklist.steps.firstChat.label",
    descriptionKey: "checklist.steps.firstChat.description",
    route: "/chat",
  },
];

function getCompletedCount(completedSteps, visibleSteps) {
  const set = new Set(completedSteps || []);
  return visibleSteps.filter((s) => set.has(s.id)).length;
}

const OnboardingChecklist = ({ open, onClose }) => {
  const { t } = useTranslation("onboarding");
  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);
  const { state, markStepCompleted } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: permData } = useEffectivePermissions();
  const scopesSet = toSet(permData?.scopes);
  const canRunGuidedSetup = canAny(scopesSet, [
    ["companies", "view"],
    ["channels", "view"],
    ["users", "view"],
    ["types", "view"],
    ["automation_rules", "view"],
  ]);

  // Filtra os passos conforme as permissões do usuário
  const visibleSteps = CORE_STEPS.filter((step) => {
    switch (step.id) {
      case "step-guided-setup":
        if (
          Array.isArray(state.completedSteps) &&
          (state.completedSteps.includes("core-onboarding-done") ||
            state.completedSteps.includes("step-guided-setup"))
        ) {
          return false;
        }
        return canRunGuidedSetup;
      case "step-connect-channels":
        return can(scopesSet, "channels", "view");
      case "step-create-users":
        return can(scopesSet, "users", "view");
      case "step-setup-ai":
        return can(scopesSet, "agents", "view") || can(scopesSet, "chatbot", "view");
      case "step-first-conversation":
        return true;
      default:
        return true;
    }
  }).map((step) => ({
    ...step,
    route:
      step.id === "step-setup-ai" && !can(scopesSet, "agents", "view") && can(scopesSet, "chatbot", "view")
        ? "/chatbots"
        : step.route,
    label: t(step.labelKey),
    description: t(step.descriptionKey),
  }));

  const completedCount = getCompletedCount(state.completedSteps, visibleSteps);
  const total = visibleSteps.length || 0;
  const progress = total > 0 ? (completedCount / total) * 100 : 0;

  const handleGoToStep = (step) => {
    navigate(step.route);
  };

  const handleMarkAllOnly = () => {
    CORE_STEPS.forEach((s) => markStepCompleted(s.id));
    markStepCompleted("core-onboarding-done");
  };

  const handleMarkAllDone = () => {
    handleMarkAllOnly();
    onClose?.();
  };

  const showCompletedBanner = total > 0 && completedCount === total;

  const isOnboardingRoute = location.pathname === "/setup";

  // Se não houver nenhum passo relevante para este usuário, não mostra o checklist
  if (visibleSteps.length === 0) return null;

  if (!open && !isOnboardingRoute) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("checklist.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("checklist.body")}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t("checklist.progressLabel")}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 0.5, height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {t("checklist.completedLine", { done: completedCount, total })}
          </Typography>
        </Box>

        {showCompletedBanner && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.success.light}`,
              backgroundColor: (theme) => theme.palette.success.light + "22",
            }}
          >
            <Typography variant="body2" color="success.main" fontWeight={600}>
              {t("checklist.doneBannerTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("checklist.doneBannerHint")}
            </Typography>
          </Box>
        )}

        <List dense>
          {visibleSteps.map((step) => {
            const isCompleted = state.completedSteps?.includes(step.id);
            return (
              <ListItem
                key={step.id}
                sx={{
                  borderRadius: 1.5,
                  mb: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleGoToStep(step)}
                  >
                    {t("checklist.goToScreen")}
                  </Button>
                }
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCompleted}
                      onChange={() => markStepCompleted(step.id)}
                    />
                  }
                  label={
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: isCompleted ? "line-through" : "none",
                          }}
                        >
                          {step.label}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {step.description}
                        </Typography>
                      }
                    />
                  }
                />
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={state.completedSteps?.includes("core-onboarding-done") || false}
                onChange={handleMarkAllOnly}
              />
            }
            label={
              <Typography variant="body2">
                {t("checklist.markAllCheckbox")}
              </Typography>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {canRunGuidedSetup ? (
          <Button onClick={() => navigate("/setup")}>{t("checklist.backToSetup")}</Button>
        ) : null}
        <Button onClick={onClose}>{t("checklist.keepExploring")}</Button>
        <Button variant="contained" onClick={handleMarkAllDone}>
          {t("checklist.markAllDone")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingChecklist;

